<template>
  <div :class="bem({ singleOption })">
    <div :class="bem('body')">
      <div :class="bem('options')">
        <FrameTypeOptions
          :view="computedView"
          :options="isStaticFrameType ? [activeOption] : allOptions"
          :customised-product="customisedProduct"
          @selected="selectedFrameType"
        />
      </div>
    </div>
    <VisionLink
      v-if="props.view !== 'full' && !singleOption"
      :class="bem('footer-btn')"
      line-link
      is-button
      :mono-font="true"
      theme="grey"
      @click="emits('toggle-frame-selector-modal-visible', true)"
    >
      Which frame?
    </VisionLink>
  </div>
</template>

<script setup>
import CustomisedProduct from '~/models/CustomisedProduct'

const bem = useBEM('FrameTypeSelectTabs')
const emits = defineEmits(['change', 'toggle-frame-selector-modal-visible'])
const { $track } = useNuxtApp()

const props = defineProps({
  customisedProduct: { type: CustomisedProduct, required: true },
  hideConversionKit: { type: Boolean, required: true },
  view: { type: String, default: 'compact' },
})

function selectedFrameType(option) {
  if (option.disabled) return

  emits('change', option.lensSku)

  $track.product.frameTypeSelected(
    props.customisedProduct,
    option.frameType || props.customisedProduct.frameType,
    !!option.conversionKit
  )

  if (option.conversionKit) {
    if (conversionKitEnabled.value) {
      props.customisedProduct.swapFrameType()
    }
    return props.customisedProduct.toggleConversionKit(true)
  } else {
    props.customisedProduct.setFrameType(option.frameType)
    props.customisedProduct.toggleConversionKit(false)
  }
}

function isActiveOption(option) {
  if (conversionKitEnabled.value) return !!option.conversionKit
  return option.frameType === frameType.value
}

function getPrice(option) {
  let price = basePrice.value

  if (option.conversionKit) {
    Object.values(partPrices.value).forEach((partPrice) => {
      price += partPrice
    })
  } else if (!isStaticFrameType.value) {
    price += partPrices.value[option.parts[0]]
  }

  return price
}

function getPriceGBP(option) {
  let price = basePriceGBP.value

  if (option.conversionKit) {
    Object.values(partPricesGBP.value).forEach((partPrice) => {
      price += partPrice
    })
  } else if (!isStaticFrameType.value) {
    price += partPricesGBP.value[option.parts[0]]
  }

  return price
}

const isStaticFrameType = computed(() => {
  return props.customisedProduct.isLimitedEdition || props.customisedProduct.isPrebuiltVariant
})

const allParts = computed(() => {
  return frameTypes.value.map((option) => option.parts || []).flat()
})

const partPrices = computed(() => {
  const allProductOptions = props.customisedProduct.isLimitedEdition
    ? props.customisedProduct.product.baseProduct?.allOptions
    : props.customisedProduct.product.allOptions

  return Object.fromEntries(
    allParts.value.map((part) => {
      const firstPartOption = Object.values(allProductOptions).find((option) =>
        option.sku.startsWith(`${part}_`)
      )

      return [part, firstPartOption.price]
    })
  )
})

const partPricesGBP = computed(() => {
  const allProductOptions = props.customisedProduct.isLimitedEdition
    ? props.customisedProduct.product.baseProduct?.allOptions
    : props.customisedProduct.product.allOptions

  return Object.fromEntries(
    allParts.value.map((part) => {
      const firstPartOption = Object.values(allProductOptions).find((option) =>
        option.sku.startsWith(`${part}_`)
      )

      return [part, firstPartOption.priceGBP]
    })
  )
})

const baseProduct = computed(() => {
  return props.customisedProduct.product.baseProduct
})

const frameType = computed(() => {
  if (isStaticFrameType.value)
    return props.customisedProduct.product.frameTypeOverride

  return props.customisedProduct.frameType
})

const conversionKitEnabled = computed(() => {
  return props.customisedProduct.conversionKitEnabled
})

const frameTypes = computed(() => {
  return props.customisedProduct.frameTypes
})

const basePrice = computed(() => {
  let basePrice = props.customisedProduct.price

  allParts.value.forEach((partSKU) => {
    if (
      conversionKitEnabled.value &&
      partSKU in props.customisedProduct.allChoices
    )
      basePrice -= props.customisedProduct.allChoices[partSKU].price
    else if (partSKU in props.customisedProduct.choices)
      basePrice -= props.customisedProduct.choices[partSKU].price
  })

  return basePrice
})

const basePriceGBP = computed(() => {
  let basePriceGBP = props.customisedProduct.priceGBP

  allParts.value.forEach((partSKU) => {
    if (
      conversionKitEnabled.value &&
      partSKU in props.customisedProduct.allChoices
    )
      basePriceGBP -= props.customisedProduct.allChoices[partSKU].priceGBP
    else if (partSKU in props.customisedProduct.choices)
      basePriceGBP -= props.customisedProduct.choices[partSKU].priceGBP
  })

  return basePriceGBP
})

const activeOption = computed(() => {
  return allOptions.value.find((option) => option.active)
})

const allOptions = computed(() => {
  // Get altered SKU for each option
  return frameTypes.value
    .map((option) => {
      const active = isActiveOption(option)

      return {
        ...option,
        active,
        price: getPrice(option),
        priceGBP: getPriceGBP(option),
        isStaticFrameType: props.customisedProduct.isLimitedEdition || props.customisedProduct.isPrebuiltVariant
      }
    })
    .filter((option) => !option.conversionKit || !props.hideConversionKit)
    .map((option) => {
      if (
        props.customisedProduct.isLimitedEdition &&
        !props.customisedProduct.product.useBaseProductName &&
        baseProduct.value
      ) {
        option.description = option.description.replace(
          baseProduct.value.name,
          props.customisedProduct.product.name
        )
      }
      return {
        ...option,
      }
    })
})

const singleOption = computed(() => {
  return allOptions.value.length === 1 || isStaticFrameType.value
})

const computedView = computed(() => {
  if (singleOption.value) {
    return 'full'
  }

  return props.view
})
</script>

<style lang="postcss">
.FrameTypeSelectTabs {
  @apply mb-10;
}

.FrameTypeSelectTabs__body {
  @apply mb-2;
}

.FrameTypeSelectTabs__footer-btn {
  @apply text-tiny ml-auto;
}
</style>
